/**
 * =============================================================================
 * IFRAME EDIT MODAL COMPONENT
 * =============================================================================
 * 
 * Modal with an iframe for editing action data.
 * Opens when user clicks "Edit" on an action confirmation card.
 * 
 * DATA FLOW:
 * 1. User clicks "Edit" on ActionDetailCard
 * 2. ChatWindow calls fetchIframeFormData() to get form configuration
 * 3. IframeEditModal opens with form data
 * 4. Data is passed to iframe via postMessage after load
 * 5. User edits in iframe, clicks confirm
 * 6. Iframe posts edited data back via postMessage
 * 7. Parent submits to API
 * 
 * API INTEGRATION POINTS:
 * - Line 150: fetchIframeFormData() - Gets form config and initial values
 * - Line 200: postMessage to iframe - Sends initial data to iframe
 * - Line 230: postMessage from iframe - Receives edited data
 * 
 * IFRAME COMMUNICATION PROTOCOL:
 * 
 * Parent → Iframe (after load):
 * ```javascript
 * iframe.contentWindow.postMessage({
 *   type: 'INIT_FORM',
 *   data: {
 *     alertId: '...',
 *     actionId: '...',
 *     formConfig: { fields: [...], values: {...} }
 *   }
 * }, '*');
 * ```
 * 
 * Iframe → Parent (on confirm):
 * ```javascript
 * window.parent.postMessage({
 *   type: 'EDIT_CONFIRM',
 *   data: { ...editedFields }
 * }, '*');
 * ```
 * 
 * Iframe → Parent (on cancel):
 * ```javascript
 * window.parent.postMessage({ type: 'EDIT_CANCEL' }, '*');
 * ```
 */

import { Box, Stack, Text, Button, Group, Modal, Loader, Alert } from '@mantine/core';
import { CheckCircle, XCircle, ArrowsOutSimple, Warning } from '@phosphor-icons/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type { AlertAction, ActionDetails } from './types';

// =============================================================================
// TYPES FOR IFRAME FORM DATA
// =============================================================================

/**
 * Form field configuration - defines what fields appear in the iframe form
 * API: Returned from GET /alerts/:alertId/actions/:actionId/form
 */
export interface IframeFormField {
    /** Field key (matches API response key) */
    key: string;
    /** Display label */
    label: string;
    /** Input type */
    type: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'email' | 'tel';
    /** Current/initial value */
    value: unknown;
    /** Is field required */
    required?: boolean;
    /** Placeholder text */
    placeholder?: string;
    /** Options for select fields */
    options?: Array<{ label: string; value: string | number }>;
    /** Validation rules */
    validation?: {
        min?: number;
        max?: number;
        pattern?: string;
        message?: string;
    };
}

/**
 * Complete form configuration for iframe
 * API: Response from GET /alerts/:alertId/actions/:actionId/form
 */
export interface IframeFormConfig {
    /** Form title */
    title: string;
    /** Form description */
    description?: string;
    /** Form fields */
    fields: IframeFormField[];
    /** Submit button label */
    submitLabel?: string;
    /** Cancel button label */
    cancelLabel?: string;
}

/**
 * Data passed to iframe on initialization
 */
export interface IframeInitData {
    alertId: string;
    actionId: string;
    actionLabel: string;
    formConfig: IframeFormConfig;
}

// =============================================================================
// PROPS
// =============================================================================

interface IframeEditModalProps {
    opened: boolean;
    url: string;
    action: AlertAction | null;
    actionDetails: ActionDetails | null;
    alertId: string;
    onConfirm: (editedData: Record<string, unknown>) => void;
    onCancel: () => void;
    isLoading: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

const IframeEditModal: React.FC<IframeEditModalProps> = memo(({
    opened,
    url,
    action,
    actionDetails,
    alertId,
    onConfirm,
    onCancel,
    isLoading,
}) => {
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [formConfig, setFormConfig] = useState<IframeFormConfig | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [isLoadingForm, setIsLoadingForm] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (!opened) {
            setIframeLoaded(false);
            setFormConfig(null);
            setFormError(null);
        }
    }, [opened]);

    // =========================================================================
    // API CALL: FETCH IFRAME FORM DATA - Line 150
    // =========================================================================
    
    /**
     * Fetches form configuration when modal opens
     * 
     * API ENDPOINT: GET /alerts/:alertId/actions/:actionId/form
     * 
     * RESPONSE FORMAT:
     * {
     *   title: "Edit Purchase Order",
     *   description: "Modify the PO details before confirming",
     *   fields: [
     *     { key: "quantity", label: "Quantity", type: "number", value: 200 },
     *     { key: "notes", label: "Notes", type: "textarea", value: "" }
     *   ],
     *   submitLabel: "Confirm Changes",
     *   cancelLabel: "Cancel"
     * }
     * 
     * TO INTEGRATE:
     * 1. Uncomment the fetch call below
     * 2. Update API_ENDPOINTS.getIframeFormData in types.ts
     * 3. Remove the dummy data return
     */
    useEffect(() => {
        if (!opened || !action || !alertId) return;

        const loadFormConfig = async () => {
            setIsLoadingForm(true);
            setFormError(null);

            try {
                // =============================================================
                // TODO: UNCOMMENT WHEN BACKEND IS READY
                // =============================================================
                // const response = await fetch(
                //     `/api/v1/alerts/${alertId}/actions/${action.id}/form`,
                //     {
                //         method: 'GET',
                //         headers: { 'Content-Type': 'application/json' },
                //         credentials: 'include',
                //     }
                // );
                // 
                // if (!response.ok) {
                //     throw new Error('Failed to load form configuration');
                // }
                // 
                // const data = await response.json();
                // 
                // // FIELD MAPPING - Update these if your API uses different keys
                // const config: IframeFormConfig = {
                //     title: data.title || data.formTitle,
                //     description: data.description || data.formDescription,
                //     fields: (data.fields || data.formFields).map((f: any) => ({
                //         key: f.key || f.fieldKey || f.name,
                //         label: f.label || f.fieldLabel || f.displayName,
                //         type: f.type || f.fieldType || 'text',
                //         value: f.value || f.defaultValue || f.initialValue || '',
                //         required: f.required || f.isRequired || false,
                //         placeholder: f.placeholder || f.hint || '',
                //         options: f.options || f.choices || [],
                //     })),
                //     submitLabel: data.submitLabel || data.confirmButtonText || 'Confirm',
                //     cancelLabel: data.cancelLabel || data.cancelButtonText || 'Cancel',
                // };
                // setFormConfig(config);
                // =============================================================

                // DUMMY DATA - Remove when backend is ready
                await new Promise((resolve) => setTimeout(resolve, 300));
                
                // Build form from actionDetails if available
                const dummyConfig: IframeFormConfig = {
                    title: `Edit: ${action.label}`,
                    description: actionDetails?.description || 'Modify the details below before confirming.',
                    fields: actionDetails?.editableFields?.map((f) => ({
                        key: f.key,
                        label: f.label,
                        type: f.type as IframeFormField['type'],
                        value: f.value,
                        required: false,
                        options: f.options,
                    })) || [
                        { key: 'quantity', label: 'Quantity', type: 'number', value: 200 },
                        { key: 'notes', label: 'Notes', type: 'textarea', value: '', placeholder: 'Add any notes...' },
                    ],
                    submitLabel: 'Confirm Changes',
                    cancelLabel: 'Cancel',
                };
                setFormConfig(dummyConfig);
                // =============================================================
                
            } catch (error) {
                console.error('Failed to load form config:', error);
                setFormError('Failed to load form. Please try again.');
            } finally {
                setIsLoadingForm(false);
            }
        };

        loadFormConfig();
    }, [opened, action, alertId, actionDetails]);

    // =========================================================================
    // SEND DATA TO IFRAME - Line 200
    // =========================================================================
    
    /**
     * Posts initial data to iframe after it loads
     * The iframe should listen for 'INIT_FORM' message and populate its form
     */
    useEffect(() => {
        if (!iframeLoaded || !formConfig || !action || !iframeRef.current) return;

        // Prepare data to send to iframe
        const initData: IframeInitData = {
            alertId,
            actionId: action.id,
            actionLabel: action.label,
            formConfig,
        };

        // =====================================================================
        // POST DATA TO IFRAME
        // The iframe should implement a message listener:
        // 
        // window.addEventListener('message', (event) => {
        //   if (event.data.type === 'INIT_FORM') {
        //     const { formConfig } = event.data.data;
        //     // Populate form fields with formConfig.fields
        //   }
        // });
        // =====================================================================
        try {
            iframeRef.current.contentWindow?.postMessage(
                { type: 'INIT_FORM', data: initData },
                '*' // In production, specify exact origin
            );
            console.log('[IframeEditModal] Sent INIT_FORM to iframe:', initData);
        } catch (error) {
            console.error('Failed to post message to iframe:', error);
        }
    }, [iframeLoaded, formConfig, action, alertId]);

    // =========================================================================
    // RECEIVE DATA FROM IFRAME - Line 230
    // =========================================================================
    
    useEffect(() => {
        if (!opened) return;

        /**
         * Handles messages from iframe
         * 
         * Expected messages:
         * - { type: 'EDIT_CONFIRM', data: { ...editedFields } }
         * - { type: 'EDIT_CANCEL' }
         * - { type: 'IFRAME_READY' } // Optional: iframe signals it's ready
         */
        const handleMessage = (event: MessageEvent) => {
            // SECURITY: In production, validate origin
            // if (event.origin !== 'https://your-iframe-domain.com') return;

            const { type, data } = event.data || {};

            switch (type) {
                case 'EDIT_CONFIRM':
                    // ========================================================
                    // IFRAME CONFIRMED - Receives edited data
                    // DATA FORMAT from iframe:
                    // { quantity: 250, notes: "Updated quantity" }
                    // 
                    // This triggers onConfirm → submitIframeEdit() in ChatWindow
                    // ========================================================
                    console.log('[IframeEditModal] Received EDIT_CONFIRM:', data);
                    onConfirm(data || {});
                    break;

                case 'EDIT_CANCEL':
                    console.log('[IframeEditModal] Received EDIT_CANCEL');
                    onCancel();
                    break;

                case 'IFRAME_READY':
                    // Iframe signals it's ready to receive data
                    // Re-send init data if we have it
                    if (formConfig && action && iframeRef.current) {
                        iframeRef.current.contentWindow?.postMessage(
                            {
                                type: 'INIT_FORM',
                                data: {
                                    alertId,
                                    actionId: action.id,
                                    actionLabel: action.label,
                                    formConfig,
                                },
                            },
                            '*'
                        );
                    }
                    break;

                case 'IFRAME_LOADED':
                    setIframeLoaded(true);
                    break;

                default:
                    // Unknown message type, ignore
                    break;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [opened, onConfirm, onCancel, formConfig, action, alertId]);

    // =========================================================================
    // FALLBACK: Manual confirm (if iframe doesn't support postMessage)
    // =========================================================================
    
    const handleFallbackConfirm = useCallback(() => {
        // Collect values from formConfig (original values if no edits via postMessage)
        const formData: Record<string, unknown> = {};
        formConfig?.fields.forEach((field) => {
            formData[field.key] = field.value;
        });
        onConfirm(formData);
    }, [onConfirm, formConfig]);

    // =========================================================================
    // RENDER
    // =========================================================================

    return (
        <Modal
            opened={opened}
            onClose={onCancel}
            title={
                <Group className="gap-2">
                    <ArrowsOutSimple size={20} weight="fill" className="text-blu-800" />
                    <Text className="font-semibold">
                        {formConfig?.title || action?.label || 'Edit Action'}
                    </Text>
                </Group>
            }
            size="xl"
            centered
            padding={0}
            classNames={{
                body: 'p-0',
                content: 'overflow-hidden',
            }}
        >
            <Stack className="gap-0">
                {/* Error State */}
                {formError && (
                    <Alert
                        color="red"
                        icon={<Warning size={16} />}
                        className="m-4"
                    >
                        {formError}
                    </Alert>
                )}

                {/* Loading Form Config */}
                {isLoadingForm && (
                    <Box className="flex items-center justify-center py-12">
                        <Stack className="items-center gap-3">
                            <Loader size="md" color="blue" />
                            <Text className="text-sm text-gray-500">Loading form...</Text>
                        </Stack>
                    </Box>
                )}

                {/* Iframe Container */}
                {!isLoadingForm && !formError && (
                    <Box className="relative min-h-[500px] bg-gray-50">
                        {!iframeLoaded && (
                            <Box className="absolute inset-0 flex items-center justify-center bg-gray-50">
                                <Stack className="items-center gap-3">
                                    <Loader size="md" color="blue" />
                                    <Text className="text-sm text-gray-500">Loading editor...</Text>
                                </Stack>
                            </Box>
                        )}

                        {/* ====================================================
                            IFRAME
                            
                            URL OPTIONS:
                            1. Static URL: action.editUrl (e.g., "/forms/edit-po")
                            2. Dynamic URL with params: `/forms/edit?alertId=${alertId}&actionId=${action.id}`
                            
                            DATA PASSING:
                            - Data is sent via postMessage after iframe loads
                            - Iframe receives { type: 'INIT_FORM', data: { formConfig } }
                            
                            INTEGRATION:
                            1. Create iframe page at the URL
                            2. Listen for 'INIT_FORM' postMessage
                            3. Populate form with formConfig.fields
                            4. On submit: postMessage({ type: 'EDIT_CONFIRM', data: {...} })
                            ==================================================== */}
                        <iframe
                            ref={iframeRef}
                            src={url}
                            title="Edit Action"
                            className={`h-[500px] w-full border-0 transition-opacity duration-200 ${
                                iframeLoaded ? 'opacity-100' : 'opacity-0'
                            }`}
                            onLoad={() => setIframeLoaded(true)}
                            sandbox="allow-scripts allow-same-origin allow-forms"
                        />
                    </Box>
                )}

                {/* Fallback Action Buttons */}
                <Box className="border-t border-gray-200 bg-gray-50 px-4 py-3">
                    <Group className="items-center justify-between">
                        <Stack className="gap-0">
                            <Text className="text-xs text-gray-500">
                                Make changes above, then confirm or cancel
                            </Text>
                            {formConfig?.description && (
                                <Text className="text-xs text-gray-400">
                                    {formConfig.description}
                                </Text>
                            )}
                        </Stack>
                        <Group className="gap-2">
                            <Button
                                variant="light"
                                color="gray"
                                size="sm"
                                leftSection={<XCircle size={14} weight="fill" />}
                                onClick={onCancel}
                                disabled={isLoading}
                            >
                                {formConfig?.cancelLabel || 'Cancel'}
                            </Button>
                            <Button
                                variant="filled"
                                color="green"
                                size="sm"
                                leftSection={<CheckCircle size={14} weight="fill" />}
                                onClick={handleFallbackConfirm}
                                loading={isLoading}
                            >
                                {formConfig?.submitLabel || 'Confirm Changes'}
                            </Button>
                        </Group>
                    </Group>
                </Box>
            </Stack>
        </Modal>
    );
});

IframeEditModal.displayName = 'IframeEditModal';

export default IframeEditModal;
